import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { MatIconsCreatorService } from '@ui';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AppVersionService, Directions, Languages, ToastService } from '@core';
import { TranslateService } from '@ngx-translate/core';
import { Direction } from '@angular/cdk/bidi';
import { EMPTY, interval } from 'rxjs';
import { VERSION } from '../../../version';
import appConfig from '@config';
import { catchError, ignoreElements, mergeMap, startWith, take, tap } from 'rxjs/operators';
import { FingerPrintService } from '@core/services/finger-print/finger-print.service';
import { ToastPayload, ToastUrl } from '@core/services/toast-service/models';
import { UrlType } from '@core/services/toast-service/enums';
import { LocalStorageItems } from '@app/core/services/localstorage/localstorage.enum';
import { MixpanelService } from '@app/core/services/mixpanel/mixpanel.service';

@Component({
	selector: 'body',
	templateUrl: './app.component.html',
	standalone: false
})
export class AppComponent implements OnInit {
	private readonly destroyRef: DestroyRef = inject(DestroyRef);

	translate = inject(TranslateService);
	private matIconRegistry = inject(MatIconRegistry);
	private domSanitizer = inject(DomSanitizer);
	private appVersionService = inject(AppVersionService);
	private toastService = inject(ToastService);
	private fingerPrintService = inject(FingerPrintService);
	private mixPanelService = inject(MixpanelService);

	title = 'lawyal-front-end';

	private isNewVersion: boolean = false;

	public direction: Direction = 'rtl';

	constructor() {
		this.fingerPrintService.assignFingerPrint();
		this.mixPanelService.init();
	}

	ngOnInit(): void {
		MatIconsCreatorService.createMatIcons(this.matIconRegistry, this.domSanitizer);
		this.init();
	}

	private init(): void {
		const storedLanguage: string = localStorage.getItem(LocalStorageItems.Language) ?? Languages.HE;
		this.translate.use(storedLanguage);
		this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.direction = this.translate.currentLang === Languages.HE ? Directions.Rtl : Directions.Ltr;
			if (this.isNewVersion) {
				this.openNewVersionToast();
			}
		});
		this.startVersionInterval();
	}

	private openNewVersionToast(): void {
		const url: ToastUrl = {
			type: UrlType.SameTab,
			urlCallback: () => location.reload()
		};
		const payload: ToastPayload = {
			toastUrl: url
		};
		const toastTranslation: { [key: string]: string } = this.translate.instant('NEW_NOTIFICATIONS.VERSION_UPDATE');
		this.toastService.customNotification(toastTranslation.MESSAGE, toastTranslation.TITLE, payload, null);
	}

	private startVersionInterval(): void {
		interval(appConfig.CHECKING_VERSION_TIMOUT)
			.pipe(
				startWith(0),
				mergeMap(() =>
					this.appVersionService.getVersion().pipe(
						tap((res: { version: string }) => {
							if (res.version !== VERSION) {
								this.openNewVersionToast();
								this.isNewVersion = true;
							}
						}),
						catchError(error => EMPTY.pipe(ignoreElements()))
					)
				),
				take(1)
				// takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}
}
